import { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useTierPricingContext } from "Payments/TierPricing/TierPricingProvider";
import { useIsDisabled } from "Payments/TierPricing/tierPricingHooks";
import { motion, useAnimation } from "framer-motion";

import FnrDeleteButton from "UI/FnrDeleteButton/FnrDeleteButton";
import FnrFade from "UI/FnrFade/FnrFade";
import FnrInput2 from "UI/FnrInput2/FnrInput2";
import FnrNumberButtons from "UI/FnrNumberButtons/FnrNumberButtons";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

const Tier = ({
  numberOfSlots,
  amount,
  setData,
  deleteData,
  tierDataBefore,
  isNew,
  minSlots,
  numberOfNonMaxTiers,
  shouldFlashSlots,
  shouldFlashAmount,
}) => {
  const [doomed, setDoomed] = useState(false);
  const { preventTierAnimation, entity, isFullWidth } = useTierPricingContext();
  const disabled = useIsDisabled();

  const setSpots = (value) => setData({ numberOfSlots: value });

  const [stagedAmount, setStagedAmount] = useState(amount);

  const updateStagedAmount = (e) => setStagedAmount(e.target.value);

  const saveNewAmount = (e) => {
    setData({ amount: e?.target?.value === "" ? null : stagedAmount });
  };

  useEffect(() => {
    setStagedAmount(amount);
  }, [amount, shouldFlashAmount]);

  const amountRef = useRef();
  const slotsRef = useRef();
  const deleteRef = useRef();

  const isMounted = useComponentIsMounted();

  const deleteIfInvisible = useCallback(
    ({ opacity }) => {
      if (opacity === 0) deleteData();
    },
    [deleteData]
  );

  const flashSlotsAnimation = useAnimation();

  useEffect(() => {
    if (shouldFlashSlots >= 1) {
      flashSlotsAnimation.stop();
      flashSlotsAnimation.start("show");
    }
  }, [flashSlotsAnimation, shouldFlashSlots]);

  const flashSlotsAnimationComplete = (animation) => {
    if (animation === "show") flashSlotsAnimation.start("hide");
  };

  const flashPriceAnimation = useAnimation();

  useEffect(() => {
    if (shouldFlashAmount >= 1) {
      flashPriceAnimation.stop();
      flashPriceAnimation.start("show");
    }
  }, [flashPriceAnimation, shouldFlashAmount]);

  const flashPriceAnimationComplete = (animation) => {
    if (animation === "show") flashPriceAnimation.start("hide");
  };

  const showUpTo =
    (!tierDataBefore && numberOfSlots > 1) ||
    (tierDataBefore && numberOfSlots - tierDataBefore.numberOfSlots > 1);

  const isPlural = numberOfSlots > 1;
  const entityText = `${entity}${isPlural ? "s" : ""}`;

  return (
    <ConditionalFnrFade
      preventTierAnimation={preventTierAnimation}
      isMounted={isMounted}
      doomed={doomed}
      deleteIfInvisible={deleteIfInvisible}
    >
      <Wrapper data-cy="TierPricing_Tier" $isFullWidth={isFullWidth}>
        <SpotsContainer>
          <FlashSlots
            animate={flashSlotsAnimation}
            initial={{ opacity: 0 }}
            variants={{
              show: { opacity: 1 },
              hide: { opacity: 0, transition: { duration: 2 } },
            }}
            onAnimationComplete={flashSlotsAnimationComplete}
          />
          <SpotsNumberContainer>
            <UpToVerbiage
              $isFullWidth={isFullWidth}
              initial={isNew && numberOfNonMaxTiers > 1 && { opacity: 0 }}
              animate={{
                opacity: showUpTo ? 1 : 0,
                scale: showUpTo ? 1 : 0.6,
                transition: showUpTo
                  ? null
                  : { type: "tween", duration: 0.2, ease: "easeOut" },
              }}
            >
              {tierDataBefore?.numberOfSlots
                ? tierDataBefore.numberOfSlots + 1
                : "Up"}{" "}
              to
            </UpToVerbiage>
            <FnrNumberButtons
              ref={slotsRef}
              type="number"
              value={numberOfSlots}
              label="Number of spots"
              setValue={setSpots}
              data-cy="Spots_Input"
              onFocus={({ target }) => {
                target.select();
              }}
              min={minSlots}
              disabled={disabled}
            />
          </SpotsNumberContainer>
          <SpotsVerbiage>{entityText}</SpotsVerbiage>
        </SpotsContainer>
        {isFullWidth && <Spacer />}
        <RightSide>
          <PriceInputContainer>
            <FlashPrice
              animate={flashPriceAnimation}
              initial={{ opacity: 0 }}
              variants={{
                show: { opacity: 1 },
                hide: { opacity: 0, transition: { duration: 2 } },
              }}
              onAnimationComplete={flashPriceAnimationComplete}
            />
            <FnrInput2
              ref={amountRef}
              type="currency"
              value={stagedAmount}
              label="Total Price"
              onChange={updateStagedAmount}
              data-cy="Amount_Input"
              onFocus={({ target }) => {
                target.select();
              }}
              onEnter={({ target }) => {
                target.blur();
              }}
              onBlur={saveNewAmount}
              disabled={disabled}
              fullWidth
              autofocus={isNew}
            />
          </PriceInputContainer>

          <DeleteButtonHolder>
            <FnrDeleteButton
              ref={deleteRef}
              onClick={() => setDoomed(true)}
              confirmText={
                !isNew && `Delete $${amount} for ${numberOfSlots} spots?`
              }
              variant="iconButton"
              disabled={disabled}
              show={true}
              style={{ fontSize: 16, width: 16 }}
            />
          </DeleteButtonHolder>
        </RightSide>
      </Wrapper>
    </ConditionalFnrFade>
  );
};

export default Tier;

const Wrapper = styled("div")`
  display: flex;
  align-items: center;
  ${({ $isFullWidth }) =>
    !$isFullWidth ? `justify-content: center; flex-direction: column;` : ""}
`;

const RightSide = styled("div")`
  display: flex;
  align-items: center;
`;

const SpotsContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  padding-left: 24px;
  flex-shrink: 0;
  position: relative;
`;
const FlashSlots = styled(motion.div)`
  position: absolute;
  background: rgba(252, 207, 107, 0.6);
  left: 8px;
  right: 0;
  top: -12px;
  bottom: -12px;
  border-radius: 12px;
  opacity: 0;
`;
const FlashPrice = styled(motion.div)`
  position: absolute;
  background: rgba(252, 207, 107, 0.6);
  left: -12px;
  right: -12px;
  top: -12px;
  bottom: -12px;
  border-radius: 20px;
  opacity: 0;
`;
const SpotsVerbiage = styled.div`
  font-size: 20px;
  margin-left: 10px;
  position: relative;
  z-index: 10;
`;
const SpotsNumberContainer = styled.div`
  position: relative;
`;
const UpToVerbiage = styled(motion.div)`
  position: absolute;
  ${({ $isFullWidth }) =>
    $isFullWidth
      ? `bottom: 100%; left: 0; width: 100%;`
      : `bottom: 30%; right: 100%; white-space: nowrap;`}
  font-size: 12px;
  text-align: center;
`;
const PriceInputContainer = styled.div`
  max-width: 120px;
  flex-shrink: 0;
  position: relative;
`;
const Spacer = styled.div`
  flex-grow: 1;
`;
const DeleteButtonHolder = styled.div`
  flex-grow: 0;
  margin-right: 16px;
  margin-left: 12px;
`;

const ConditionalFnrFade = ({
  preventTierAnimation,
  isMounted,
  doomed,
  deleteIfInvisible,
  children,
}) => {
  if (preventTierAnimation) {
    return <>{children}</>;
  }

  return (
    <FnrFade
      show={isMounted && !doomed}
      onAnimationComplete={deleteIfInvisible}
    >
      {children}
    </FnrFade>
  );
};
